import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import {
  AllVueFormValues,
  ConnectionType,
  allVueConfigSchema,
  getDefaultAllVueConfig,
} from './allVueConfig.schema';
import { useAllVueConfigActions } from './useAllVueConfigActions';
import { trackEvent } from '../../../util/datadogTracking';
import { AppName } from '../../../data-models/integrations-app.data-model';

export function useAllVueConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { config, isConnected, serverConnectionType, isLoading, saveConfig, resetConfig } =
    useAllVueConfigActions();

  const isExistingOAuthConfig = isConnected && serverConnectionType === 'oauth';
  const schema = allVueConfigSchema(isExistingOAuthConfig);
  const methods = useForm<AllVueFormValues>({
    resolver: yupResolver(schema as ObjectSchema<AllVueFormValues>),
    defaultValues: getDefaultAllVueConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  // Populate form when config loads from server
  useEffect(() => {
    if (config) {
      const connectionType: ConnectionType = config.connectionType === 'oauth' ? 'oauth' : 'snowflake';
      reset({
        connectionType,
        database: config.config?.database || '',
        clientId: config.config?.clientId || '',
        clientSecret: '',
        tokenEndpoint: config.config?.tokenEndpoint || '',
      });
    }
  }, [config, reset]);

  useEffect(() => {
    if (isDirty && editStatus === 'initial') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('AllVue Config', errors);
      return;
    }
    try {
      const result = await saveConfig(getValues());
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getValues());
      trackEvent('integration.connected', { provider: AppName.AllVue, success: true });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setErrorMessage(axiosError?.response?.data?.message || 'Failed to configure AllVue connection');
      setSuccessMessage('');
      trackEvent('integration.connected', { provider: AppName.AllVue, success: false });
    }
  }, [trigger, saveConfig, getValues, errors, reset]);

  const onReset = useCallback(async () => {
    await resetConfig();
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetConfig]);

  return {
    editStatus,
    isConnected,
    serverConnectionType,
    isLoading,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
